import { useNavigate } from '@tanstack/react-router';
import { PROGRAM_KIND_LABEL, SUPPORT_VIEW_LABEL, type GrantSupportFilter, type ProgramKind } from '@zoxo/shared';

import { PageContainer } from '../../components/layout/page-container';
import { GrantList } from './grant-list';
import type { GrantListLoaderData } from './list-loader';
import { writeListCookie } from './list-state';
import type { GrantFilters } from './use-grants';
import { pageHead, PUBLISHER_LD } from '../../lib/seo';
import { canonical, SITE_DESCRIPTION } from '../../lib/site';

const typeLabel = (type: GrantSupportFilter, kind?: ProgramKind) =>
  kind ? `${SUPPORT_VIEW_LABEL[type]} · ${PROGRAM_KIND_LABEL[kind]}` : SUPPORT_VIEW_LABEL[type];

/** 기둥별 목록의 head(). 설명은 사이트 설명 — 크롤러가 보던 값 그대로다. */
export function grantTypeHead(type: GrantSupportFilter, path: string, kind?: ProgramKind) {
  return pageHead({
    title: `${typeLabel(type, kind)} 공고 — 정부지원사업`,
    description: SITE_DESCRIPTION,
    url: canonical(path),
    type: 'website',
    jsonLd: [{ '@context': 'https://schema.org', ...PUBLISHER_LD }],
  });
}

/**
 * 기둥별 목록 — `/news/grants` `/news/loans` `/news/facilities` `/news/programs` · `/news/programs/<slug>`.
 *
 * **보조금을 찾는 사람과 융자를 찾는 사람은 다른 사람이다.** 한 목록에 필터
 * 드롭다운으로 묻어 두면 「융자만 보기」가 두 번 클릭 깊이에 숨는다. 주소가
 * 따로 있으면 공유되고, 검색엔진이 각각을 색인하고, 즐겨찾기가 된다.
 *
 * **`/news` 와 같은 목록을 쓴다.** 유형만 고정할 뿐이라 필터·정렬·무한스크롤이
 * 전부 같다. 화면을 따로 만들면 한쪽만 고쳐지고, 그때부터 두 목록이 다른
 * 세상을 보여준다.
 */
export function GrantTypePage({
  type,
  kind,
  path,
  filters,
  loaded,
}: {
  type: GrantSupportFilter;
  /** 프로그램 하위 종류. 주소(`/news/programs/<slug>`)가 정한다 — 검색 파라미터가 아니다. */
  kind?: ProgramKind;
  path: string;
  filters: GrantFilters;
  /** 라우트 loader(loadGrantList)가 정한 유효 상태와 기준 시각. */
  loaded: GrantListLoaderData;
}) {
  const navigate = useNavigate();
  const label = typeLabel(type, kind);
  // URL 이 이기고, 없으면 쿠키(마지막 선택), 없으면 기본 — loader 가 정했다(서버와 같은 값).
  const effective: GrantFilters = { ...filters, ...loaded.state, kind };

  return (
    <PageContainer allowOverflow>
      {/* 제목은 머리의 알약이 말한다. 화면에는 필터 막대와 목록만 — 낭독기·검색엔진용으로만 남긴다. */}
      <h1 className="sr-only">{label}</h1>

      <div>
        {/*
          **유형은 URL 이 정한다.** 목록의 유형 필터를 여기서 쓰면 `/loans` 에서
          「보조금」을 골랐을 때 주소와 화면이 어긋난다 — 주소는 융자인데 보조금이
          보인다. 유형을 바꾸는 것은 다른 주소로 가는 일이다.
        */}
        <GrantList
          filters={{ ...effective, support: type }}
          now={new Date(loaded.now)}
          onChange={(next) => {
            writeListCookie(next);
            void navigate({
              to: path,
              search: {
                ...(next.region ? { region: next.region } : {}),
                ...(next.field ? { field: next.field } : {}),
                ...(next.closed && next.closed !== 'exclude' ? { closed: next.closed } : {}),
                ...(next.sort && next.sort !== 'latest' ? { sort: next.sort } : {}),
                // 검색어가 빠져 있었다 — Enter 를 쳐도 주소가 안 바뀌어 목록이 그대로였다.
                ...(next.q ? { q: next.q } : {}),
              },
              replace: true,
            });
          }}
          lockSupport
        />
      </div>
    </PageContainer>
  );
}
